/**
 * Single place where build-time configuration is read and where the runtime configuration is applied (TASK-0076).
 *
 * Reading `import.meta.env` in exactly one module keeps the environmental surface auditable (and testable, since a test
 * only has to stub one module rather than the global `import.meta`). The build-time values are only *defaults* now: the
 * deployed `config.json` decides, and {@link applyRuntimeConfig} is the one function that overwrites them, called once
 * from `configureRuntime` before React renders. Everything else in the application reads `env` at the moment it needs a
 * value — never a copy taken at module load — which is what makes the runtime address authoritative.
 */

import {
  DEFAULT_REFRESH_SKEW_SECONDS,
  DEFAULT_TIMEOUT_MS,
  type RuntimeConfig,
} from '@/lib/runtimeConfig';

const DEFAULT_API_BASE_URL = '/api/v1';

/** Parses a positive integer from configuration, falling back when the value is absent or unusable. */
function readPositiveInt(raw: string | undefined, fallback: number): number {
  if (!raw) {
    return fallback;
  }

  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/** Normalizes the base URL so a trailing slash can never produce a doubled path segment. */
export function normalizeApiBaseUrl(raw: string | undefined): string {
  const trimmed = raw?.trim();

  if (!trimmed) {
    return DEFAULT_API_BASE_URL;
  }

  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
}

/** The address and behaviour the application is running with. */
export interface AppEnvironment {
  /** Base URL of the API, including its `/api/v1` segment. */
  apiBaseUrl: string;
  /** Timeout of an ordinary API call; SSE streams are excluded. */
  apiTimeoutMs: number;
  /** Lead time before access-token expiry at which a request rotates the token proactively. */
  tokenRefreshSkewSeconds: number;
}

export const env: AppEnvironment = {
  apiBaseUrl: normalizeApiBaseUrl(import.meta.env.VITE_API_BASE_URL),
  apiTimeoutMs: readPositiveInt(import.meta.env.VITE_API_TIMEOUT_MS, DEFAULT_TIMEOUT_MS),
  tokenRefreshSkewSeconds: readPositiveInt(
    import.meta.env.VITE_TOKEN_REFRESH_SKEW_SECONDS,
    DEFAULT_REFRESH_SKEW_SECONDS,
  ),
};

/**
 * Replaces the build-time defaults with the values a deployment configured.
 *
 * Mutating one object rather than exporting a getter keeps every existing call site (`${env.apiBaseUrl}/auth/refresh`,
 * `baseURL: env.apiBaseUrl`) reading the current value with no change, and keeps exactly one source of truth: an API
 * module cannot invent a second one because it has no way to reach `import.meta.env`.
 *
 * @param config The resolved runtime configuration.
 */
export function applyRuntimeConfig(config: RuntimeConfig): void {
  env.apiBaseUrl = normalizeApiBaseUrl(config.apiBaseUrl);
  env.apiTimeoutMs = config.apiTimeoutMs;
  env.tokenRefreshSkewSeconds = config.tokenRefreshSkewSeconds;
}

/**
 * Restores the build-time defaults.
 *
 * Exists for tests, which must not let one case's runtime configuration leak into the next; production code applies the
 * configuration once and never resets it.
 */
export function resetRuntimeConfig(): void {
  env.apiBaseUrl = normalizeApiBaseUrl(import.meta.env.VITE_API_BASE_URL);
  env.apiTimeoutMs = readPositiveInt(import.meta.env.VITE_API_TIMEOUT_MS, DEFAULT_TIMEOUT_MS);
  env.tokenRefreshSkewSeconds = readPositiveInt(
    import.meta.env.VITE_TOKEN_REFRESH_SKEW_SECONDS,
    DEFAULT_REFRESH_SKEW_SECONDS,
  );
}

/**
 * Where the application looks for its runtime configuration.
 *
 * `BASE_URL` is the one build-time value that must stay baked in: it is where the application *is* (Vite's `base`), and it
 * is how the same bundle works under a sub-path. The file always sits beside `index.html`.
 */
export function runtimeConfigUrl(): string {
  return (
    import.meta.env.VITE_RUNTIME_CONFIG_URL?.trim() || `${import.meta.env.BASE_URL}config.json`
  );
}

/**
 * The environment name the configuration rules are judged against.
 *
 * `VITE_APP_ENV` wins so a deployment can be explicit; otherwise Vite's own `PROD` flag decides, which is `true` for
 * `vite build` and `false` for `vite dev`. Anything that is not `development` is treated as production for the purpose of
 * the "no fallback" rule — the safe direction.
 */
export function appEnvironment(): string {
  return (
    import.meta.env.VITE_APP_ENV?.trim() || (import.meta.env.PROD ? 'Production' : 'Development')
  );
}
